import { useEffect, useRef, useCallback } from 'react';
import { getVideoURL } from '../../BackendInterface';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
// Styles
import 'video.js/dist/video-js.css';
import { logDebugLevel } from '../../GlobalFunctions';
import { Dialog, DialogContent } from '../Dialog';

interface IVideoPlayerProps {
  options?: videojs.PlayerOptions;
  videoId: string;
  isShow: boolean;
  onHide: () => void;
  title?: string;
}

const initialOptions: videojs.PlayerOptions = {
  controls: true,
  fluid: true,
  controlBar: {
    volumePanel: {
      inline: false,
    },
  },
  width: 560,
  autoplay: true,
};

const MuxPlayer = ({ options, videoId, isShow, onHide, title }: IVideoPlayerProps) => {
  const videoNode = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Player | null>(null);

  const getMuxURL = useCallback(async () => {
    // the video node may not be created yet, especially if
    // the video is embedded in a modal that is not yet shown
    if (!videoNode.current) {
      logDebugLevel('Video node not created yet, returning');
      return;
    }

    logDebugLevel('Attempting to initialize video player with videoId:', videoId);
    const muxURLResponse = await getVideoURL(videoId);

    playerRef.current = videojs(videoNode.current, {
      ...initialOptions,
      ...(options as object),
      sources: [{ src: muxURLResponse, type: 'application/x-mpegURL' }],
    });
  }, [videoId]);

  useEffect(() => {
    getMuxURL();
  }, [isShow]);

  return (
    <Dialog
      open={isShow}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          internalOnHide();
        }
      }}
    >
      <DialogContent
        className='z-[9000] w-[min(95vw,960px)] rounded-[0.6rem] border-0 bg-white p-0 text-[1.6rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]'
        overlayClassName='z-[9000] bg-black/55'
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className='relative border-b border-[#dee2e6] px-[1.6rem] pb-[1.2rem] pt-[1.4rem]'>
          <button
            type='button'
            onClick={internalOnHide}
            aria-label='Close'
            className='absolute right-[1rem] top-[0.7rem] border-0 bg-transparent p-0 text-[2.4rem] leading-none text-[#242424]'
          >
            x
          </button>
          <h3 className='m-0 text-[2rem] leading-[1.2] text-[#45A8E4]'>{title}</h3>
        </div>
        <div className='p-[1.6rem]'>
          <div className='w-full'>
            <video ref={videoNode} className='video-js w-full' />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  function internalOnHide() {
    const player = playerRef.current;

    logDebugLevel('intneralOnHide Disposing video player but checking if it exists...', player);
    if (player && !player.isDisposed()) {
      logDebugLevel('internalOnHide Disposing video player');
      player.dispose();
      playerRef.current = null;
    }
    onHide();
  }
};

export default MuxPlayer;
